/**
 * Phase 7 Commit J. Staff step — avatar tiles, "Any available" framed
 * as smart default not downgrade. Tap-to-advance (App's selectStaff
 * commits the cart change + advances to Time).
 */

import type { BootstrapResponse } from '../types';

interface Props {
  bootstrap: BootstrapResponse;
  selectedServiceId: string | null;
  selectedStaffId: string | null;
  onSelect: (staffId: string | null) => void;
  /**
   * Staff is tap-to-advance with no RunningBar, so it was the ONLY step in the
   * flow with no submit lock. selectStaff tears every line out of the cart and
   * re-adds them; a double-tap ran two of those sequences concurrently, the
   * second reading a stale cart from its render closure and re-adding lines
   * against a cart the first was still mutating — duplicate/orphaned lines.
   */
  submitting?: boolean;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function StaffStep({
  bootstrap,
  selectedServiceId,
  selectedStaffId,
  onSelect,
  submitting = false,
}: Props) {
  // One gate for both the pointer and keyboard paths — a disabled-looking card
  // that still fires on Enter is the same bug wearing a hat.
  const pick = (id: string | null) => {
    if (submitting) return;
    onSelect(id);
  };
  const service = bootstrap.services.find((s) => s.id === selectedServiceId);
  const eligibleStaffIds =
    service && service.staffIds.length > 0 ? service.staffIds : null;
  const staff = eligibleStaffIds
    ? bootstrap.staff.filter((t) => eligibleStaffIds.includes(t.id))
    : bootstrap.staff;

  return (
    <div class="sbw-scroll">
      <div class="sbw-eyebrow">Pick a stylist</div>
      <h2 class="sbw-step-title">Who&apos;d you like in your chair?</h2>
      <p class="sbw-step-sub">
        Or let us match you with the soonest opening — same hands-on team
        either way.
      </p>

      <div class="sbw-staff-list">
        {bootstrap.capabilities.anyStaffBooking && (
          <div
            class={
              'sbw-staff' +
              (selectedStaffId === null ? ' is-selected' : '') +
              (submitting ? ' is-busy' : '')
            }
            aria-disabled={submitting}
            onClick={() => pick(null)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                pick(null);
              }
            }}
          >
            <div class="sbw-avatar is-any">✦</div>
            <div class="sbw-staff-body">
              <div class="sbw-staff-name">First available stylist</div>
              <div class="sbw-staff-bio">
                Soonest opening across the team
              </div>
            </div>
          </div>
        )}
        {staff.map((t) => {
          const isSelected = selectedStaffId === t.id;
          return (
            <div
              key={t.id}
              class={
                'sbw-staff' +
                (isSelected ? ' is-selected' : '') +
                (submitting ? ' is-busy' : '')
              }
              aria-disabled={submitting}
              onClick={() => pick(t.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  pick(t.id);
                }
              }}
            >
              {t.imageUrl ? (
                <img class="sbw-avatar" src={t.imageUrl} alt={t.displayName} />
              ) : (
                <div class="sbw-avatar">{initials(t.displayName)}</div>
              )}
              <div class="sbw-staff-body">
                <div class="sbw-staff-name">{t.displayName}</div>
                {t.description && (
                  <div class="sbw-staff-bio">{t.description}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
